"use client";
import { Reveal } from "../reveal";
import { ExtractBySectionType, SectionType } from "types";
import { PortableText } from "next-sanity";
import { MobileMockup } from "../mobile-mockup";
import { SanityImage } from "../sanity-image";
import { useMediaQuery } from "@/hooks/useMediaQuery";

type HowItWorksType = ExtractBySectionType<SectionType, "howItWorks", "_type">;

export function HowItWorks({ data }: { data: HowItWorksType }) {
  const { heading, subheading, steps, image } = data;
  const isMobile = useMediaQuery("(max-width: 768px)");
  const isTablet = useMediaQuery("(max-width: 1024px)");

  return (
    <section className="max-w-screen-2xl mx-auto py-12 sm:py-16 lg:py-20 px-4 sm:px-6 lg:px-10 xl:px-24 bg-white">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-10 lg:gap-12 items-start lg:items-center max-w-screen-lg mx-auto">
        {/* Content Section */}
        <div className="flex flex-col gap-6 sm:gap-8 relative order-2 lg:order-1">
          {/* Header */}
          <div className="text-center lg:text-start mb-6 sm:mb-8 lg:mb-12">
            <h3 className="font-medium text-[#0D0D0D] text-xl sm:text-2xl lg:text-3xl xl:text-4xl leading-[120%] font-outfit mb-2 sm:mb-3">
              {heading}
            </h3>
            {subheading && (
              <p className="text-xs sm:text-sm lg:text-base text-gray-600 max-w-md mx-auto lg:mx-0">
                {subheading}
              </p>
            )}
          </div>

          {/* Steps */}
          <div className="flex flex-col gap-6 sm:gap-8">
            {steps?.map((item, index) => (
              <Reveal key={index} className="flex gap-3 sm:gap-4 relative">
                {/* Step Number and Connector */}
                <div className="flex flex-col items-center flex-shrink-0">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 bg-primary-500 rounded-full flex items-center justify-center mb-1">
                    <span className="text-white font-bold text-sm sm:text-base lg:text-lg">
                      {String(index + 1).padStart(2, "0")}
                    </span>
                  </div>
                  {index !== steps.length - 1 && (
                    <div className="w-[2px] flex-1 bg-primary-200 mt-2 min-h-[40px] sm:min-h-[60px]" />
                  )}
                </div>

                {/* Step Content */}
                <div className="flex flex-col gap-2 sm:gap-3 flex-1 pb-4 sm:pb-6">
                  <h4 className="text-sm sm:text-base lg:text-xl xl:text-2xl leading-tight font-medium text-[#0D0D0D] pr-2">
                    {item.title}
                  </h4>
                  <PortableText
                    value={item.description}
                    components={{
                      block: {
                        normal: ({ children }) => (
                          <p className="text-sm lg:text-base xl:text-lg text-[#808080] leading-relaxed pr-2">
                            {children}
                          </p>
                        ),
                      },
                    }}
                  />
                </div>
              </Reveal>
            ))}
          </div>
        </div>

        {/* Mobile Mockup Section */}
        <div className="flex justify-center lg:justify-end order-1 lg:order-2 mb-6 lg:mb-0">
          <div className="relative">
            <MobileMockup
              width={isMobile ? 280 : isTablet ? 320 : 400}
              height={isMobile ? 568 : isTablet ? 650 : 811}
            >
              {image && (
                <div className="flex justify-center items-center w-full h-full p-2">
                  <SanityImage
                    src={image}
                    alt={image.alt || "How it works demonstration"}
                    fill
                    className="object-contain rounded-lg"
                    priority
                    sizes="(max-width: 768px) 280px, (max-width: 1024px) 320px, 400px"
                  />
                </div>
              )}
            </MobileMockup>
          </div>
        </div>
      </div>
    </section>
  );
}
